// @flow
import './Modal2.css';

import React from 'react';
import Portal from './../common/Portal';
import classNames from 'classnames';
import _ from 'lodash';
import Spinner from '../common/Spinner';
import Button from '../common/Button';
import KeyboardShortcuts from '../keyboard-shortcuts/KeyboardShortcuts';
import KeydownConfig from '../keyboard-shortcuts/KeydownConfig';
import { KeyCode } from '../util/Constants';

export type FooterButton = {
  onClick: Function,
  label?: string,
  className?: string,
  icon?: string,
  type?: string,
  title?: string,
  disabled?: boolean
};

type Props = {
  // The dialog content
  children?: any,

  // The dialog title
  title?: string,

  // The dialog title icon
  titleIcon?: string,

  // If specifying the title isn't custom enough, the
  // header can be completely overridden
  header?: any,

  // The footer buttons
  buttons?: FooterButton[],

  // If specifying the footer buttons isn't custom enough, the
  // footer can be completely overridden
  footer?: any,

  // onClose callback
  onClose: Function,

  // Specify a [name] attribute to the dialog's main
  // content DOM node (optional)
  name?: string,

  // Set `loading: true` to display a spinner instead of the dialog content
  loading?: boolean,

  // Set to `true` to allow the dialog to be dismissed by
  // clicking anywhere in the background
  closeOnOutsideClick?: boolean,

  closeOnEsc?: boolean,
  dropdownValue?: string
};

export type ModalProps = Props;

type State = {
  scrollable: boolean
};

export default class Modal2 extends React.Component<Props, State> {
  static defaultProps = {
    name: '',
    title: '',
    loading: false,
    closeOnOutsideClick: true,
    onClose: _.noop,
    closeOnEsc: true,
    dropdownValue: '',
    showCrossIcon: true
  };

  _node: ?HTMLDivElement = null;
  _bodyNode: ?HTMLDivElement = null;
  state = {
    scrollable: false
  };

  componentDidMount() {
    if (this._node) {
      const input = this._node.querySelector('input[type="text"]') || this._node.querySelector('input');
      if (this.props.dropdownValue == '' && input && input.focus) {
        input.focus();
      }
    }

    this.checkHeight();
    KeyboardShortcuts.add(new KeydownConfig('MODAL', this.props.onClose, [KeyCode.ESCAPE]));
    KeyboardShortcuts.add(
      new KeydownConfig(
        'MODAL',
        () => {},
        [KeyCode.UP, KeyCode.DOWN, KeyCode.LEFT, KeyCode.RIGHT, KeyCode.I, KeyCode.O],
        [],
        true,
        true,
        true
      )
    );
  }

  componentDidUpdate() {
    this.checkHeight();
  }
  componentWillUnmount() {
    KeyboardShortcuts.remove('MODAL');
  }

  checkHeight = () => {
    if (this._bodyNode) {
      const scrollable = this._bodyNode.scrollHeight > this._bodyNode.clientHeight;
      if (scrollable !== this.state.scrollable) {
        this.setState({ scrollable });
      }
    }
  };

  onClick = (event: MouseEvent) => {
    const { closeOnOutsideClick, onClose } = this.props;
    if (event.target === event.currentTarget && closeOnOutsideClick) {
      event.stopPropagation();
      onClose();
    }
  };

  renderHeader = () => {
    const { title, titleIcon, header, onClose, showCrossIcon, onCross } = this.props;

    if (header) {
      return header;
    }

    if (!(title || titleIcon)) {
      return null;
    }

    return (
      <header>
        <div className="title">
          {titleIcon ? <img src={titleIcon} className="title-icon" /> : null}
          <span>{title}</span>
        </div>
        {showCrossIcon ? (
          <Button className="close" icon="images/close_gray.svg" onClick={onCross ? onCross : onClose} />
        ) : null}
      </header>
    );
  };

  renderBody = () => {
    const { loading, children } = this.props;
    const body = loading ? (
      <div className="flex-row align-center justify-center" style={{ height: 100 }}>
        <Spinner large />
      </div>
    ) : (
      children
    );

    return (
      <div className="content" ref={(node) => (this._bodyNode = node)}>
        {body}
      </div>
    );
  };

  renderFooter = () => {
    const { footer, buttons } = this.props;
    if (footer) {
      return footer;
    }

    if (!buttons) {
      return null;
    }

    return (
      <footer className={classNames({ center: buttons.length === 1 })}>
        {buttons.map((b, i) => (
          <Button
            key={i}
            text={b.label}
            type={b.type}
            className={b.className}
            disabled={b.disabled}
            onClick={b.onClick}
            style={b.style ? b.style : {}}
          />
        ))}
      </footer>
    );
  };

  onContextMenuClick = (e: MouseEvent) => {
    // e.preventDefault();
    e.stopPropagation();
  };

  render() {
    const { name, buttons = [] } = this.props;
    const { scrollable } = this.state;

    return (
      <Portal
        defaultOpen={true}
        onClose={this.props.onClose}
        closeOnOutsideClick={this.props.closeOnOutsideClick}
        closeOnEsc={this.props.closeOnEsc}>
        <div
          name="Modal2"
          className={classNames({ scrollable, 'single-button': buttons.length === 1 })}
          onContextMenu={this.onContextMenuClick}>
          <div ref={(node) => (this._node = node)} onClick={this.onClick} className="background">
            <div className="modal2" name={name} style={this.props.style ? this.props.style : null}>
              {this.renderHeader()}
              {this.renderBody()}
              {this.renderFooter()}
            </div>
          </div>
        </div>
      </Portal>
    );
  }
}
